/**
 * Work Sample correction SLA — pure UI calculation (no worker / notifications).
 * Business rule: TOR has CORRECTION_SLA_BUSINESS_DAYS weekdays from the flag date to address.
 * Weekends are skipped; org holiday calendars are not applied yet.
 */

import { CORRECTION_SLA_BUSINESS_DAYS } from '../constants/workSampleWorkflow';

function startOfLocalDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function isWeekend(date) {
  const day = date.getDay();
  return day === 0 || day === 6;
}

/** Parse ISO / Date / YYYY-MM-DD into a local Date, or null. */
export function parseSlaDate(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const raw = String(value).trim();
  const dayOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dayOnly) {
    return new Date(Number(dayOnly[1]), Number(dayOnly[2]) - 1, Number(dayOnly[3]));
  }
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Add N business days after `from` (does not count the from day itself). */
export function addBusinessDays(from, n) {
  const d = startOfLocalDay(from);
  let remaining = Math.max(0, Number(n) || 0);
  while (remaining > 0) {
    d.setDate(d.getDate() + 1);
    if (!isWeekend(d)) remaining -= 1;
  }
  return d;
}

/** Count business days in (from, to] — 0 when to <= from. */
export function countBusinessDaysAfter(from, to) {
  const start = startOfLocalDay(from);
  const end = startOfLocalDay(to);
  if (end.getTime() <= start.getTime()) return 0;
  let count = 0;
  const cursor = new Date(start);
  while (cursor.getTime() < end.getTime()) {
    cursor.setDate(cursor.getDate() + 1);
    if (!isWeekend(cursor)) count += 1;
  }
  return count;
}

/**
 * @param {{ flaggedAt: string|Date, asOf?: string|Date|null, businessDays?: number }} opts
 * @returns {null | {
 *   dueAt: Date,
 *   flaggedAt: Date,
 *   asOf: Date,
 *   businessDays: number,
 *   businessDaysLeft: number,
 *   overdueByBusinessDays: number,
 *   isOverdue: boolean,
 *   isDueToday: boolean,
 *   label: string,
 *   shortLabel: string,
 * }}
 */
export function getFlagCorrectionSla({
  flaggedAt,
  asOf = new Date(),
  businessDays = CORRECTION_SLA_BUSINESS_DAYS,
} = {}) {
  const flagged = parseSlaDate(flaggedAt);
  if (!flagged) return null;

  const asOfDate = parseSlaDate(asOf) || new Date();
  const flaggedDay = startOfLocalDay(flagged);
  const asOfDay = startOfLocalDay(asOfDate);
  const days = Math.max(1, Number(businessDays) || CORRECTION_SLA_BUSINESS_DAYS);
  const dueAt = addBusinessDays(flaggedDay, days);

  if (asOfDay.getTime() === dueAt.getTime()) {
    return {
      dueAt,
      flaggedAt: flaggedDay,
      asOf: asOfDay,
      businessDays: days,
      businessDaysLeft: 0,
      overdueByBusinessDays: 0,
      isOverdue: false,
      isDueToday: true,
      label: 'Due today to address this flag',
      shortLabel: 'Due today',
    };
  }

  if (asOfDay.getTime() > dueAt.getTime()) {
    const overdueBy = countBusinessDaysAfter(dueAt, asOfDay);
    const unit = overdueBy === 1 ? 'business day' : 'business days';
    return {
      dueAt,
      flaggedAt: flaggedDay,
      asOf: asOfDay,
      businessDays: days,
      businessDaysLeft: 0,
      overdueByBusinessDays: overdueBy,
      isOverdue: true,
      isDueToday: false,
      label: `Overdue by ${overdueBy} ${unit} — flag not addressed`,
      shortLabel: `Overdue ${overdueBy}d`,
    };
  }

  const left = countBusinessDaysAfter(asOfDay, dueAt);
  if (left >= days && asOfDay.getTime() === flaggedDay.getTime()) {
    const unit = days === 1 ? 'business day' : 'business days';
    return {
      dueAt,
      flaggedAt: flaggedDay,
      asOf: asOfDay,
      businessDays: days,
      businessDaysLeft: left,
      overdueByBusinessDays: 0,
      isOverdue: false,
      isDueToday: false,
      label: `You have ${days} ${unit} to address this flag`,
      shortLabel: `${days}d to address`,
    };
  }

  const unit = left === 1 ? 'business day' : 'business days';
  return {
    dueAt,
    flaggedAt: flaggedDay,
    asOf: asOfDay,
    businessDays: days,
    businessDaysLeft: left,
    overdueByBusinessDays: 0,
    isOverdue: false,
    isDueToday: false,
    label: `${left} ${unit} left to address this flag`,
    shortLabel: `${left}d left`,
  };
}

/** Convenience: SLA for a comment/flag row that has created_at. */
export function getCommentFlagCorrectionSla(flag, asOf = new Date()) {
  if (!flag) return null;
  const status = String(flag.metadata?.flag_status || 'open').toLowerCase();
  if (status && status !== 'open') return null;
  return getFlagCorrectionSla({
    flaggedAt: flag.created_at || flag.flagged_at || flag.metadata?.flagged_at,
    asOf,
  });
}
